import './DcsPublishingScreen.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// Set is aliased so the native Set stays reachable: CircuitsList works in a native Set of
// device ids, while the chosen points are an Immutable one.
import { List, Map, Set as ImmutableSet } from 'immutable';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import Button from 'src/component/UI/Button';
import CircuitsList from 'src/component/UI/CircuitsList';
import ConfirmModal from 'src/component/UI/ConfirmModal';
import PermissionFence from 'src/component/PermissionFence';
import { useTranslation } from 'src/i18n';
import { hasPermissions } from 'src/module/authentication/selector';
import {
  fetchDcsPublication, fetchDcsDataPoints, fetchDcsMap,
  createDcsPublication, updateDcsPublication, addDcsPoints, removeDcsPoint, reassignDcsAddresses,
  resetDcsPoints
} from 'src/module/dcsPublication/action';
import {
  getDcsPublication, getDcsPoints, getDcsDataPoints, getDcsPublicationLoading
} from 'src/module/dcsPublication/selector';
import { moveRow, findConflicts, MapRow } from './mapLayout';

// separate Modbus address spaces: a row can be reordered within its table, never across
const TABLES = ['input_register', 'input_coil'];

const toRow = (p): MapRow => ({ id: p.get('id'), address: p.get('address'), length: p.get('length') });

/**
 * What this facility publishes to a DCS.
 *
 * One publication per facility: every frog client here with its Modbus slave switched on serves
 * the same mapping. Protocol is not chosen here - the facility decides what to publish, the frog
 * client decides how to expose it.
 *
 * Three tabs: choose objects, choose what to publish from them, then lay out the resulting map.
 */
export default function DcsPublishingScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const publication = useSelector(getDcsPublication);
  const loading = useSelector(getDcsPublicationLoading);
  const points = useSelector(getDcsPoints);
  const catalogue = useSelector(getDcsDataPoints);
  const canEdit: boolean = useSelector(hasPermissions('edit-dcs-publication'));

  const [tab, setTab] = useState('objects');

  // CircuitsList works in a native Set of device ids, the same way the device group page drives it
  const [selected, setSelected] = useState(new Set([]));
  const [selectedPoints, setSelectedPoints] = useState(ImmutableSet());

  // map editing is deliberately modal: it gives up the stability the generated map provides,
  // so it should not be something a stray click or drag can do. Drags and typed addresses build
  // up in a draft per table, and only Save sends them.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, MapRow[]>>({});
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    dispatch(fetchDcsPublication());
    dispatch(fetchDcsMap());
  }, []);

  const selectedIds = useMemo(() => List(Array.from(selected)), [selected]);

  // the API merges the catalogue across whatever factories the selection spans
  useEffect(() => {
    dispatch(fetchDcsDataPoints(selectedIds));
  }, [selectedIds]);

  const pointsById = useMemo(() => points.reduce((acc, p) => acc.set(p.get('id'), p), Map()), [points]);

  const conflicts = useMemo(() => TABLES.reduce((acc, table) => {
    findConflicts(draft[table] || []).forEach(id => acc.add(id));
    return acc;
  }, new Set<number>()), [draft]);

  const moves = useMemo(() => TABLES.flatMap(table => (draft[table] || [])
    .filter(r => r.address !== pointsById.get(r.id)?.get('address'))), [draft, pointsById]);

  const updateSelected = (newSelected) => setSelected(new Set(newSelected));

  const removeSelected = (toRemove) => {
    const next = new Set(selected);
    toRemove.forEach(id => next.delete(id));
    setSelected(next);
  };

  const togglePoint = (source, key) => {
    const id = `${source}|${key}`;
    setSelectedPoints(selectedPoints.has(id) ? selectedPoints.remove(id) : selectedPoints.add(id));
  };

  const handleCreate = () => dispatch(createDcsPublication(Map({ name: 'DCS Publication' })));
  const patch = (changes) => dispatch(updateDcsPublication(Map(changes)));

  const handleAdd = () => {
    const selections = selectedPoints.toList().map((id: any) => {
      const [source, ...rest] = id.split('|');
      return Map({ source, key: rest.join('|') });
    });
    dispatch(addDcsPoints(selectedIds, selections));
    setSelected(new Set([]));
    setSelectedPoints(ImmutableSet());
    setTab('map');
  };

  const startEditing = () => {
    setDraft(TABLES.reduce((acc, table) => ({
      ...acc,
      [table]: points.filter(p => p.get('point_type') === table).map(toRow).toArray()
    }), {}));
    setEditing(true);
  };

  const typeAddress = (table, id, value) => setDraft({
    ...draft,
    [table]: draft[table].map(r => r.id === id ? { ...r, address: value === '' ? NaN : Number(value) } : r)
  });

  const onDragEnd = ({ source, destination }) => {
    // a drop outside the list, or into the other table, changes nothing
    if (!destination || destination.droppableId !== source.droppableId) {
      return;
    }
    const table = source.droppableId;
    setDraft({ ...draft, [table]: moveRow(draft[table], source.index, destination.index) });
  };

  const saveAddresses = () => {
    // send only what actually moved; the API validates the resulting map as a whole
    if (moves.length) {
      dispatch(reassignDcsAddresses(List(moves.map(r => Map({ id: r.id, address: r.address })))));
    }
    setEditing(false);
  };

  // a reset starts the map over, so go back to where a map starts
  const handleReset = () => {
    dispatch(resetDcsPoints());
    setConfirmingReset(false);
    setTab('objects');
  };

  /** The generated map as CSV - what a DCS integrator commissions against. */
  const handleExport = () => {
    const rows = [['table', 'address', 'length', 'source', 'data_point', 'device_tag', 'controller_tag', 'device_factory']];
    points.forEach((p) => {
      rows.push([
        p.get('point_type'), p.get('address'), p.get('length'), p.get('source'),
        p.get('data_point'), p.get('device_tag') ?? '', p.get('controller_tag') ?? '',
        p.get('device_factory') ?? ''
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `dcs-modbus-map-${publication?.get('name') || 'facility'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Standard points carry an explicit label key; status and settings keys go through the same
   * lookup, which already holds translations for many of them and falls back to a humanised form
   * of the key for the rest.
   */
  const pointLabel = (entry) => t(entry.get('labelKey') || entry.get('key'));

  const renderPointGroup = (title, entries) => {
    if (!entries.size) {
      return null;
    }
    return (
      <div className='dcs-point-group' key={title}>
        <h5>{title}</h5>
        {/* one per row, flowing into columns so a large factory map stays scannable */}
        <div className='dcs-point-columns'>
          {entries.map((entry) => {
            const id = `${entry.get('source')}|${entry.get('key')}`;
            return (
              <FormControlLabel className='dcs-point' key={id} label={pointLabel(entry)}
                control={<Checkbox size='small' checked={selectedPoints.has(id)}
                  onChange={() => togglePoint(entry.get('source'), entry.get('key'))} />}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderCells = (table, row: MapRow, dragHandleProps?) => {
    const p = pointsById.get(row.id);
    const original = p.get('address');
    return (
      <>
        {editing && (
          <TableCell className='dcs-drag-handle' {...dragHandleProps}>
            <DragIndicatorIcon fontSize='small' />
          </TableCell>
        )}
        <TableCell>
          {editing ? (
            <>
              <TextField size='small' type='number' className='dcs-address-input'
                value={Number.isFinite(row.address) ? row.address : ''}
                onChange={(e) => typeAddress(table, row.id, e.target.value)} />
              {row.address !== original && <div className='dcs-was'>{t('dcsPublishing.was', { address: original })}</div>}
            </>
          ) : row.address}
        </TableCell>
        <TableCell>{row.length}</TableCell>
        <TableCell>{p.get('device_tag')}</TableCell>
        <TableCell>{p.get('source')}</TableCell>
        <TableCell>{t(p.get('data_point'))}</TableCell>
        <TableCell>
          <PermissionFence can='edit-dcs-publication'>
            <Button className='dcs-remove' disabled={editing}
              icon={<DeleteIcon style={{ fontSize: 18 }} />}
              onClick={() => dispatch(removeDcsPoint(row.id))} />
          </PermissionFence>
        </TableCell>
      </>
    );
  };

  const rowClass = (row: MapRow, dragging = false) => [
    row.address !== pointsById.get(row.id)?.get('address') && 'dcs-moved',
    conflicts.has(row.id) && 'dcs-conflict',
    dragging && 'dcs-dragging'
  ].filter(Boolean).join(' ');

  const renderTable = (table) => {
    const rows: MapRow[] = editing
      ? draft[table] || []
      : points.filter(p => p.get('point_type') === table).map(toRow).toArray();
    if (!rows.length) {
      return null;
    }

    return (
      <div className='dcs-map-table' key={table}>
        <h5>{t(table === 'input_coil' ? 'dcsPublishing.inputCoils' : 'dcsPublishing.inputRegisters')}</h5>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {editing && <TableCell />}
              <TableCell>Address</TableCell>
              <TableCell>Len</TableCell>
              <TableCell>Object</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Data point</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          {/* outside editing there is nothing to drag, so the rows are plain */}
          {!editing ? (
            <TableBody>
              {rows.map(row => <TableRow key={row.id}>{renderCells(table, row)}</TableRow>)}
            </TableBody>
          ) : (
            <Droppable droppableId={table}>
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {rows.map((row, index) => (
                    <Draggable key={row.id} draggableId={String(row.id)} index={index}>
                      {(provided, snapshot) => (
                        <TableRow ref={provided.innerRef} {...provided.draggableProps}
                          className={rowClass(row, snapshot.isDragging)}>
                          {renderCells(table, row, provided.dragHandleProps)}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          )}
        </Table>
      </div>
    );
  };

  if (loading && !publication) {
    return <div className='page' id='dcs-publishing'><p>{t('common.loading')}</p></div>;
  }

  // Nothing configured is a normal state, not an error: the frogs' Modbus surface is just empty.
  if (!publication) {
    return (
      <div className='page' id='dcs-publishing'>
        <Helmet><title>{t('dcsPublishing.title')}</title></Helmet>
        <h2>{t('dcsPublishing.title')}</h2>
        <Paper className='dcs-empty'>
          <p>{t('dcsPublishing.notConfigured')}</p>
          <PermissionFence can='edit-dcs-publication'>
            <Button cta onClick={handleCreate}>{t('dcsPublishing.setUp')}</Button>
          </PermissionFence>
        </Paper>
      </div>
    );
  }

  const registers = points.filter(p => p.get('point_type') === 'input_register');
  const coils = points.filter(p => p.get('point_type') === 'input_coil');

  // enabling mid-edit would publish the saved map, not the draft on screen
  const enableBlockedBy = publication.get('enabled') ? 'dcsPublishing.alreadyEnabled'
    : editing ? 'dcsPublishing.saveBeforeEnabling' : null;

  // without edit rights the first two tabs have nothing to offer
  const activeTab = canEdit ? tab : 'map';

  return (
    <div className='page' id='dcs-publishing'>
      <Helmet><title>{t('dcsPublishing.title')}</title></Helmet>
      <h2>{t('dcsPublishing.title')}</h2>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className='dcs-section'>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label='Name' defaultValue={publication.get('name')}
                  onBlur={(e) => patch({ name: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth type='number' label='Refresh (seconds)'
                  defaultValue={publication.get('refresh_seconds')}
                  onBlur={(e) => patch({ refresh_seconds: parseInt(e.target.value, 10) })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label='Temperature unit'
                  value={publication.get('temperature_unit') || 'C'}
                  onChange={(e) => patch({ temperature_unit: e.target.value })}>
                  <MenuItem value='C'>Celsius</MenuItem>
                  <MenuItem value='F'>Fahrenheit</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  label={publication.get('enabled') ? 'Enabled' : 'Disabled'}
                  control={<Switch checked={Boolean(publication.get('enabled'))}
                    onChange={(e) => patch({ enabled: e.target.checked })} />}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className='dcs-section'>
            {/* a draft map is laid out against the current points, so adding more mid-edit
                would leave it stale - the earlier steps wait until it is saved or cancelled */}
            <Tabs value={activeTab} onChange={(e, value) => setTab(value)} className='dcs-tabs'
              textColor='primary' indicatorColor='primary'>
              {canEdit && <Tab value='objects' disabled={editing}
                label={`1. ${t('dcsPublishing.selectObjects')}${selected.size ? ` (${selected.size})` : ''}`} />}
              {canEdit && <Tab value='points' disabled={editing}
                label={`2. ${t('dcsPublishing.addDataPoints')}${selectedPoints.size ? ` (${selectedPoints.size})` : ''}`} />}
              <Tab value='map' label={`${canEdit ? '3. ' : ''}${t('dcsPublishing.orderTheMap')} (${points.size})`} />
            </Tabs>

            {/* the same list the device group page uses, so selecting devices here behaves
                exactly as it does there - filtering, paging and select-all included */}
            {activeTab === 'objects' && (
              <>
                <CircuitsList
                  pagination
                  filterable
                  multiSelect
                  showModel
                  selected={selected}
                  updateSelected={updateSelected}
                  removeSelected={removeSelected}
                  banner={!selected.size ? null : `${selected.size} selected`}
                />
                <div className='button-bar'>
                  <Button cta disabled={!selected.size} onClick={() => setTab('points')}>
                    {t('dcsPublishing.next')}
                  </Button>
                </div>
              </>
            )}

            {activeTab === 'points' && (
              !selected.size ? <p className='dcs-count'>{t('dcsPublishing.selectObjectsFirst')}</p> : (
                <>
                  {renderPointGroup(t('dcsPublishing.standardPoints'), catalogue.get('standard') || List())}
                  {renderPointGroup(t('dcsPublishing.statusPoints'), catalogue.get('status') || List())}
                  {renderPointGroup(t('dcsPublishing.settingPoints'), catalogue.get('setting') || List())}
                  <div className='button-bar'>
                    <Button cta disabled={!selectedPoints.size} onClick={handleAdd}>
                      Add {selected.size * selectedPoints.size} point(s)
                    </Button>
                  </div>
                </>
              )
            )}

            {activeTab === 'map' && (
              <>
                <div className='dcs-section-header'>
                  <p className='dcs-count'>
                    {registers.size} input register point(s), {coils.size} input coil(s).
                  </p>
                  <div>
                    <PermissionFence can='edit-dcs-publication'>
                      {editing ? (
                        <>
                          <Button onClick={() => setEditing(false)}>{t('dcsPublishing.cancelEdit')}</Button>
                          <Button cta disabled={conflicts.size > 0} onClick={saveAddresses}>
                            {t('dcsPublishing.saveAddresses')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button disabled={!points.size} onClick={() => setConfirmingReset(true)}>
                            {t('dcsPublishing.resetMap')}
                          </Button>
                          <Button disabled={!points.size} onClick={startEditing}>{t('dcsPublishing.editAddresses')}</Button>
                        </>
                      )}
                    </PermissionFence>
                    <Button onClick={handleExport} disabled={!points.size || editing}>{t('dcsPublishing.exportCsv')}</Button>
                  </div>
                </div>

                {editing && (
                  <div className='dcs-alerts'>
                    <Alert severity='warning'>{t('dcsPublishing.addressWarning')}</Alert>
                    {publication.get('enabled') && (
                      <Alert severity='error'>{t('dcsPublishing.addressWarningEnabled')}</Alert>
                    )}
                    <Alert severity='info'>{t('dcsPublishing.dragHint')}</Alert>
                    {moves.length > 0 && (
                      <Alert severity='warning'>{t('dcsPublishing.pointsWillMove', { count: moves.length })}</Alert>
                    )}
                    {conflicts.size > 0 && (
                      <Alert severity='error'>{t('dcsPublishing.addressConflicts')}</Alert>
                    )}
                  </div>
                )}

                {!points.size ? <p>{t('dcsPublishing.nothingPublished')}</p> : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    {TABLES.map(renderTable)}
                  </DragDropContext>
                )}

                <PermissionFence can='edit-dcs-publication'>
                  <div className='button-bar'>
                    {/* a disabled button fires no hover events, so the tooltip hangs off a wrapper */}
                    <Tooltip title={enableBlockedBy ? t(enableBlockedBy) : ''}>
                      <span className='dcs-tooltip-target'>
                        <Button cta disabled={Boolean(enableBlockedBy)} onClick={() => patch({ enabled: true })}>
                          {t('dcsPublishing.enable')}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                </PermissionFence>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* typed confirmation: this is the one action on the page that cannot be undone */}
      <ConfirmModal
        open={confirmingReset}
        title={t('dcsPublishing.resetMapTitle')}
        message={publication.get('enabled')
          ? `${t('dcsPublishing.resetWarning')} ${t('dcsPublishing.addressWarningEnabled')}`
          : t('dcsPublishing.resetWarning')}
        confirmPhrase='RESET'
        handleConfirm={handleReset}
        handleReject={() => setConfirmingReset(false)}
        handleClose={() => setConfirmingReset(false)}
      />
    </div>
  );
}
